import type { TFunction } from 'i18next';

import type { ActionItem } from '@/components/actions-button/ActionsButton';

type BuildResultActionItemsParams = {
  t: TFunction<'common', 'indicator_results'>;
  canAddResult: boolean;
  canDeleteResult: boolean;
  canExport: boolean;
  hasSelectedResults: boolean;
  onAddResult: () => void;
  onDeleteResult: () => void;
  onExportImage: () => void;
};

/**
 * Builds the ordered Add / Delete / Export items for the indicator results
 * Actions dropdown.
 *
 * Permission-gated actions are HIDDEN when the user lacks the permission —
 * matching the platform convention for dropdown actions (cf. the controls
 * Actions dropdown), rather than rendering them disabled. The only disabled
 * state is Delete with no selection: the user has delete permission, but the
 * delete flow operates over the selected results, so the item stays visible
 * and disabled with a contextual tooltip until rows are selected.
 */
export const buildResultActionItems = ({
  t,
  canAddResult,
  canDeleteResult,
  canExport,
  hasSelectedResults,
  onAddResult,
  onDeleteResult,
  onExportImage,
}: BuildResultActionItemsParams): ActionItem[] => {
  const items: ActionItem[] = [];

  if (canAddResult) {
    items.push({
      id: 'addResult',
      text: t('create_new_button'),
      onItemClick: onAddResult,
    });
  }

  if (canDeleteResult) {
    items.push({
      id: 'deleteResult',
      text: t('delete_button'),
      disabled: !hasSelectedResults,
      disabledReason: hasSelectedResults
        ? undefined
        : t('actions.delete_disabled_no_selection'),
      onItemClick: onDeleteResult,
    });
  }

  if (canExport) {
    items.push({
      id: 'exportImage',
      text: t('actions.export_image_button'),
      onItemClick: onExportImage,
    });
  }

  return items;
};
